import { useEffect, useState, type ReactNode } from "react";
import { apiFetch, ensureFreshAccessForSso, getTenantHost, refreshAccessToken, storeTenantHost, tokenStore } from "@/integrations/api/client";
import {
  ERP_SSO_PAYLOAD,
  ERP_SSO_READY,
  clearErpSsoPending,
  clearSsoQuery,
  isErpSsoLaunch,
  markErpSsoPending,
  portalOriginGuess,
  takeHashAccess,
  tenantPortalLoginUrl,
} from "@/integrations/api/sso";
import { FullscreenLoader } from "@/components/ui/loader";

let ssoDone = false;
let ssoInFlight: Promise<void> | null = null;

async function establishSession(access: string, refresh?: string | null, tenantHost?: string | null) {
  if (refresh) tokenStore.set(access, refresh);
  else tokenStore.set(access, tokenStore.getRefresh() || "");
  storeTenantHost(tenantHost);
  tokenStore.set(access, refresh || tokenStore.getRefresh() || "");
  clearSsoQuery();
  clearErpSsoPending();
  ssoDone = true;

  const attemptLogin = async (token: string) => apiFetch("/api/auth/token-login/", {
    method: "POST",
    body: JSON.stringify({
      access: token,
      refresh: refresh || null,
      tenant_host: tenantHost || getTenantHost() || null,
    }),
  });

  void (async () => {
    try {
      let activeAccess = await ensureFreshAccessForSso(access, refresh);
      tokenStore.set(activeAccess, refresh || tokenStore.getRefresh() || "");
      let res = await attemptLogin(activeAccess);
      if (!res.ok && refresh) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          activeAccess = refreshed;
          tokenStore.set(activeAccess, refresh);
          res = await attemptLogin(activeAccess);
        }
      }
    } catch {
      /* Bearer JWT is enough */
    }
  })();
}

function applyHashSessionSync(): boolean {
  if (ssoDone) return true;
  const hashed = takeHashAccess();
  if (!hashed?.access) return false;
  tokenStore.set(hashed.access, hashed.refresh || "");
  storeTenantHost(hashed.tenantHost);
  clearSsoQuery();
  clearErpSsoPending();
  ssoDone = true;
  void establishSession(hashed.access, hashed.refresh, hashed.tenantHost);
  return true;
}

function waitForPortalPayload(timeoutMs = 8000): Promise<{ access: string; refresh?: string; tenantHost?: string }> {
  return new Promise((resolve, reject) => {
    const portalOrigin = portalOriginGuess();
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Sign-in timed out. Please open HAMS again from the portal."));
    }, timeoutMs);

    function onMessage(event: MessageEvent) {
      const allowed = (() => {
        if (event.origin === portalOrigin) return true;
        if (
          event.origin === "https://dev-hospital-sso.gksinfotech.com"
          || /^https:\/\/[\w.-]+\.dev-hospital-sso\.gksinfotech\.com$/.test(event.origin)
          || event.origin === "https://hospital-sso.zitrapps.com"
          || /^https:\/\/[\w.-]+\.hospital-sso\.zitrapps\.com$/.test(event.origin)
        ) {
          return true;
        }
        try {
          const host = new URL(event.origin).hostname.toLowerCase();
          return host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost");
        } catch {
          return false;
        }
      })();
      if (!allowed) return;
      if (!event.data || event.data.type !== ERP_SSO_PAYLOAD || !event.data.access) return;

      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve({ access: event.data.access, refresh: event.data.refresh, tenantHost: event.data.tenantHost });
    }

    window.addEventListener("message", onMessage);

    try {
      if (window.opener && !window.opener.closed) {
        const origins = new Set<string>([
          portalOrigin,
          "http://localhost:3000",
          "http://127.0.0.1:3000",
          "http://localhost:3010",
          "http://127.0.0.1:3010",
          "https://dev-hospital-sso.gksinfotech.com",
          "https://hospital-sso.zitrapps.com",
        ]);
        try {
          if (document.referrer) origins.add(new URL(document.referrer).origin);
        } catch {
          /* ignore */
        }
        const host = window.location.hostname;
        if (host.endsWith(".localhost")) {
          origins.add(`http://${host}:3000`);
          origins.add(`http://${host}:3010`);
        }
        for (const origin of origins) {
          window.opener.postMessage({ type: ERP_SSO_READY }, origin);
        }
      } else {
        window.clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        reject(new Error("Please open HAMS from the organisation portal (popup handoff required)."));
      }
    } catch {
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      reject(new Error("Please open HAMS from the organisation portal."));
    }
  });
}

export function SsoBootstrap({ children }: { children: ReactNode }) {
  const [ssoBootstrapping, setSsoBootstrapping] = useState(
    () => typeof window !== "undefined" && !ssoDone && !applyHashSessionSync() && isErpSsoLaunch(),
  );
  const [ssoError, setSsoError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sso_logout") === "1") {
      clearErpSsoPending();
      tokenStore.clear();
      ssoDone = false;
      void apiFetch("/api/auth/logout/", { method: "POST" }).finally(() => {
        document.documentElement.innerHTML = "";
      });
      return;
    }

    if (ssoDone) {
      setSsoBootstrapping(false);
      return;
    }

    if (applyHashSessionSync()) {
      setSsoBootstrapping(false);
      return;
    }

    if (!isErpSsoLaunch() && params.get("erp_sso") !== "1") {
      setSsoBootstrapping(false);
      return;
    }

    markErpSsoPending();

    if (!ssoInFlight) {
      ssoInFlight = (async () => {
        const hashed = takeHashAccess();
        if (hashed?.access) {
          await establishSession(hashed.access, hashed.refresh, hashed.tenantHost);
          return;
        }
        if (window.opener && !window.opener.closed) {
          const payload = await waitForPortalPayload(8000);
          await establishSession(payload.access, payload.refresh, payload.tenantHost);
          return;
        }
        throw new Error("Please open HAMS from the organisation portal.");
      })().finally(() => {
        ssoInFlight = null;
      });
    }

    let cancelled = false;
    ssoInFlight
      .then(() => {
        if (cancelled) return;
        setSsoError("");
        setSsoBootstrapping(false);
        if (window.location.pathname === "/" || window.location.pathname === "/auth") {
          window.history.replaceState({}, "", "/dashboard");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        clearSsoQuery();
        clearErpSsoPending();
        const message = err instanceof Error ? err.message : "Could not complete sign-in.";
        setSsoError(message);
        setSsoBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (ssoBootstrapping) {
    return <FullscreenLoader />;
  }
  if (ssoError) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface-soft px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">Could not open HAMS</h1>
          <p className="mt-2 text-sm text-muted-foreground">{ssoError}</p>
          <a href={tenantPortalLoginUrl()} className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Return to portal
          </a>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
